import type { PagedEnvelope } from '@bookora/ui';

import { apiGet } from '@/api/mutator';
import { branchParam, type StaffBranchRef } from '@/features/branches/contract';

import {
  rolesCatalogue,
  rolesCreate,
  rolesList,
  rolesRemove,
  rolesUpdate,
} from '@/api/generated/roles/roles';
import {
  scheduleCreateTimeOff,
  scheduleListTimeOff,
  scheduleRemoveTimeOff,
  scheduleReplaceWorkingHours,
} from '@/api/generated/schedule/schedule';
import {
  staffActivate,
  staffAssignRole,
  staffDeactivate,
  staffGet,
  staffInvite,
  staffList as staffListRaw,
  staffListInvites,
  staffResendInvite,
  staffRevokeInvite,
  staffSeatUsage,
  staffSetPermissions,
  staffSetRevenueShare,
  staffUpdate,
  staffWorkingHoursApplyBulk,
} from '@/api/generated/staff/staff';
import type {
  AssignRoleDto,
  BulkWorkingHoursDto,
  BulkWorkingHoursResponse,
  CreateRoleDto,
  CreateTimeOffDto,
  InviteStaffDto,
  ReplaceWorkingHoursDto,
  RoleResponse,
  SeatUsageResponse,
  SetPermissionOverridesDto,
  SetRevenueShareDto,
  StaffInviteCreatedResponse,
  StaffInviteResponse,
  StaffListParams,
  StaffResponse,
  TimeOffResponse,
  UpdateRoleDto,
  UpdateStaffDto,
  WorkingHoursResponse,
} from '@/api/generated/model';

/**
 * The staff + roles surface, as this feature actually calls it.
 *
 * Almost everything is a straight re-export: one import site per endpoint
 * means `pnpm gen:api` breaking a signature breaks here and nowhere else.
 *
 * The single correction left is {@link fetchStaff}. `GET /staff` is annotated
 * `@ApiOkResponse({ type: [StaffResponse] })` but returns a
 * `PagedResult<StaffResponse>`, so the generated signature says "array" and
 * the wire says "envelope". Consuming it as an array is `undefined.map` at
 * runtime, in a screen the type-checker declared fine. Restating the real
 * shape once here keeps that lie out of every call site.
 *
 * (The `{ [key: string]: unknown }` nullable fields this file used to work
 * around are gone — the `@nestjs/swagger` CLI plugin now emits `string | null`
 * — so those corrections have been deleted rather than left as dead scaffolding.)
 */

export type StaffMember = StaffResponse;
export type Role = RoleResponse;
/**
 * `staffName` is the invited number's name in this clinic, so a pending row
 * says WHO has not signed in yet (null when the number has no staff record).
 */
export type StaffInvite = StaffInviteResponse;
export type SeatUsage = SeatUsageResponse;
export type WorkingHoursRow = WorkingHoursResponse;
export type TimeOffRow = TimeOffResponse;
export type InviteCreated = StaffInviteCreatedResponse;

/* -------------------------------------------------------------------------- */
/* Reads                                                                      */
/* -------------------------------------------------------------------------- */

export { rolesCatalogue, staffGet, staffSeatUsage as fetchSeatUsage };

/** `GET /staff` — really paged, whatever the generated signature says. */
export function fetchStaff(
  params: StaffListParams = {},
  signal?: AbortSignal,
): Promise<PagedEnvelope<StaffMember>> {
  return staffListRaw(params, signal);
}

/**
 * Every member of the clinic, active and inactive, across every page.
 *
 * The role editor has to answer "how many people hold this role" and "is this
 * the last owner" — both are counts over the WHOLE clinic, and a paged view
 * would answer them from whichever page happened to be loaded. `pageSize` is
 * capped at `MAX_PAGE_SIZE` (100) on every list endpoint platform-wide — see
 * `backend/src/common/dto/pagination.dto.ts` — deliberately, so a list route
 * can never double as an unaudited bulk export. A clinic past 100 seats just
 * costs this function a second request; the cap itself is not ours to raise.
 */
export async function fetchAllStaff(signal?: AbortSignal): Promise<StaffMember[]> {
  const pageSize = 100;
  const first = await fetchStaff({ page: 1, pageSize }, signal);
  const items = [...first.items];
  for (let page = 2; page <= first.pageCount; page += 1) {
    const next = await fetchStaff({ page, pageSize }, signal);
    items.push(...next.items);
  }
  return items;
}

export function fetchRoles(signal?: AbortSignal): Promise<Role[]> {
  return rolesList(signal);
}

export function fetchInvites(signal?: AbortSignal): Promise<StaffInvite[]> {
  return staffListInvites(signal);
}

/**
 * `GET /staff/:id/working-hours?branchId=` — one colleague's week, at one
 * branch when given (a multi-branch clinic edits each site's roster apart).
 * Hand-written for the `branchId` query the committed contract does not carry
 * yet; the URL is the generated function's own.
 */
export function fetchStaffWorkingHours(
  staffId: string,
  branchId?: string | null,
  signal?: AbortSignal,
): Promise<WorkingHoursRow[]> {
  return apiGet<WorkingHoursRow[]>(
    `/staff/${encodeURIComponent(staffId)}/working-hours`,
    branchId ? { branchId } : undefined,
    signal,
  );
}

/** `GET /working-hours?branchId=` — everyone's shifts, at one branch when given. */
export function fetchClinicWorkingHours(
  branchId?: string | null,
  signal?: AbortSignal,
): Promise<WorkingHoursRow[]> {
  return apiGet<WorkingHoursRow[]>('/working-hours', branchParam(branchId), signal);
}

/**
 * The branches a colleague works at (`StaffResponse.branches`, primary
 * first), or `null` when the response predates the field — the caller then
 * treats them as working everywhere it may see.
 */
export function staffBranches(member: object): StaffBranchRef[] | null {
  const branches = (member as { branches?: unknown }).branches;
  return Array.isArray(branches) ? (branches as StaffBranchRef[]) : null;
}

/** `GET /time-off` is paged (`{ items, … }`); the screen wants the flat list. */
export function fetchTimeOff(
  params: { staffId?: string; from?: string; to?: string } = {},
  signal?: AbortSignal,
): Promise<TimeOffRow[]> {
  return scheduleListTimeOff({ ...params, pageSize: 100 }, signal).then((page) => page.items);
}

/* -------------------------------------------------------------------------- */
/* Writes                                                                     */
/* -------------------------------------------------------------------------- */

export const staffApi = {
  update: (id: string, dto: UpdateStaffDto) => staffUpdate(id, dto),
  assignRole: (id: string, dto: AssignRoleDto) => staffAssignRole(id, dto),
  setOverrides: (id: string, dto: SetPermissionOverridesDto) => staffSetPermissions(id, dto),
  setRevenueShare: (id: string, dto: SetRevenueShareDto) => staffSetRevenueShare(id, dto),
  activate: (id: string) => staffActivate(id),
  deactivate: (id: string) => staffDeactivate(id),
};

export const inviteApi = {
  create: (dto: InviteStaffDto): Promise<InviteCreated> => staffInvite(dto),
  resend: (id: string) => staffResendInvite(id),
  revoke: (id: string) => staffRevokeInvite(id),
};

export const roleApi = {
  create: (dto: CreateRoleDto) => rolesCreate(dto),
  update: (id: string, dto: UpdateRoleDto) => rolesUpdate(id, dto),
  remove: (id: string) => rolesRemove(id),
};

export const scheduleApi = {
  /**
   * Replaces the colleague's week AT ONE BRANCH when `branchId` is given — the
   * server leaves their shifts at every other branch alone.
   */
  replaceWorkingHours: (staffId: string, dto: ReplaceWorkingHoursDto, branchId?: string | null) =>
    scheduleReplaceWorkingHours(staffId, { ...dto, ...branchParam(branchId) }),
  /**
   * One shift on the chosen weekdays of several colleagues at once. Unlike the
   * per-colleague PUT, this replaces ONLY the listed weekdays (`replaceDays`)
   * and leaves each person's other days and breaks alone, in one transaction.
   */
  applyBulk: (
    dto: BulkWorkingHoursDto,
    branchId?: string | null,
  ): Promise<BulkWorkingHoursResponse> =>
    staffWorkingHoursApplyBulk({ mode: 'replaceDays', ...dto, ...branchParam(branchId) }),
  createTimeOff: (dto: CreateTimeOffDto) => scheduleCreateTimeOff(dto),
  removeTimeOff: (id: string) => scheduleRemoveTimeOff(id),
};

/**
 * `staffRemove` (`DELETE /staff/:id`) is deliberately NOT re-exported.
 *
 * It is a soft end-of-membership on the server, but the word "delete" beside a
 * colleague's name in a medical system reads as "erase their history", and this
 * panel offers «غیرفعال‌سازی» instead — same effect on access, honest label.
 * Nothing in this product hard-deletes; the button should not imply otherwise.
 */
